import { useCopies } from "../hooks/useCopies";
import Button from "../components/button";
import useConsentBannerContainerService from "./service";
import { ConsentManagementServiceType } from "./types";
import Icon from "assets/icons";
import React from "react";
import { ConsentProviderType, useConsent } from "context/consent";
import { ConsentAcceptanceType } from "shared/enums/ConsentAcceptanceType";
import { ConsentType, consentManagement } from "./consent";
import Slider from "shared/components/slider";
import Direction from "shared/enums/Direction";
import Variant from "shared/enums/Variant";
import classNames from "classnames";
import ToggleButton from "shared/form/core/toggleButton";
import OneTrustConsent from "./oneTrust";
import { CookieManager } from "./enum";
import { LoadThirdPartyScripts, useLoadThirdPartyScripts } from "./scripts";
import { isInIframe } from "shared/utils/iframe";

export default function ConsentBannerContainer() {
  const service = useConsentBannerContainerService();

  const cookieComponent = React.useMemo(() => {
    if (isInIframe) {
      // if something is getting displayed in iframe then we don't want to show cookies banner
      return <LoadThirdPartyScripts />;
    }

    if (service.consentManagementType === CookieManager.IN_HOUSE) {
      return <ConsentBannerContainerInHouse service={service} />;
    }
    if (service.consentManagementType === CookieManager.UNKNOWN) {
      return null;
    }
    if (service.consentManagementType === CookieManager.ONE_TRUST) {
      return <OneTrustConsent service={service} />;
    }

    if (service.consentManagementType === CookieManager.ALLOW_ALL) {
      return <LoadThirdPartyScripts />;
    }

    return null;
  }, [service]);

  return cookieComponent;
}

export function ConsentBannerContainerInHouse({ service }: { service: ConsentManagementServiceType }) {
  const copies = useCopies();
  const [isOpen, setIsOpen] = React.useState(service.askForConsent);
  const [isManageOpen, setIsManageOpen] = React.useState(false);
  useLoadThirdPartyScripts();

  React.useEffect(() => {
    if (service.askForConsent) {
      !consentManagement.hasConsentCookie() && setIsOpen(true);
    }
  }, [service.askForConsent]);

  if (!service.canShowCookieConsentButton) {
    return null;
  }

  if (isOpen) {
    if (isManageOpen) {
      return (
        <ManageCookies
          setIsOpen={setIsOpen}
          setIsManageOpen={setIsManageOpen}
          analyticalConsentProvided={service.analyticalConsentProvided}
          hasAnyConsent={service.hasAnyConsent}
          grantConsent={service.grantConsent}
        />
      );
    }
    return (
      <div className="consentContainer">
        <div className="consentContainer__content">
          <div className="consentContainer__content_left">
            <span className="consentContainer__content--icon">
              <Icon.Oreo />
            </span>
            <div
              className="consentContainer__content--copy"
              dangerouslySetInnerHTML={{
                __html: service.consentCopy
              }}
            />
          </div>
          <div className="consentContainer__content_right">
            <div className="consentContainer__content--action">
              <Button
                className="secondary"
                isOutline
                label={copies.get("manage")}
                onClick={() => {
                  setIsManageOpen(true);
                }}
              />
              <Button
                label={copies.get("yes.i.accept")}
                onClick={() => {
                  service.acceptClickHandler();
                  setIsOpen(false);
                }}
              />
            </div>
          </div>
        </div>
      </div>
    );
  } else {
    return (
      <React.Fragment>
        <button
          title="Cookies"
          className="cookiesButton"
          onClick={() => {
            setIsOpen(c => !c);
            if (consentManagement.hasConsentCookie()) {
              setIsManageOpen(true);
            }
          }}>
          <Icon.Oreo />
        </button>
      </React.Fragment>
    );
  }
}

function ManageCookies({
  setIsManageOpen,
  hasAnyConsent,
  analyticalConsentProvided,
  grantConsent,
  setIsOpen
}: {
  setIsManageOpen: React.Dispatch<React.SetStateAction<boolean>>;
  hasAnyConsent: boolean;
  analyticalConsentProvided: boolean;
  grantConsent: ConsentProviderType["grantConsent"];
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
}) {
  const handleConfirmChoice = React.useCallback(
    (consent: ConsentType) => {
      grantConsent(ConsentAcceptanceType.CLICKED, {
        ...consentManagement.getConsentStatus(),
        ...consent
      });
      setIsManageOpen(false);
      setIsOpen(false);
    },
    [grantConsent, setIsManageOpen, setIsOpen]
  );
  const copies = useCopies();

  return (
    <Slider
      parentClassName="sliderConsentWrapper"
      className="sliderConsent"
      title={
        <React.Fragment>
          <Icon.Oreo /> {copies.get("slider.consent.heading.title")}
        </React.Fragment>
      }
      direction={Direction.LEFT}
      handleClose={
        consentManagement.hasConsentCookie()
          ? () => {
              setIsManageOpen(false);
              setIsOpen(false);
            }
          : undefined
      }>
      <Accordian analyticalConsentProvided={analyticalConsentProvided} handleConfirmChoice={handleConfirmChoice} />
    </Slider>
  );
}

enum CookieType {
  OPTIONAL,
  REQUIRED
}

function Accordian({
  analyticalConsentProvided,
  handleConfirmChoice
}: {
  analyticalConsentProvided: boolean;
  handleConfirmChoice: (consent: ConsentType) => void;
}) {
  const copies = useCopies();
  const { hasStoredCookieConsent } = useConsent();
  const [state, setState] = React.useState<ConsentType>({
    necessary: true,
    analytical: hasStoredCookieConsent ? analyticalConsentProvided : true,
    functional: false,
    targeting: false
  });
  const data = React.useMemo(() => {
    return [
      {
        isOpen: false,
        header: copies.get("cookie.category.necessary.header"),
        body: copies.get("cookie.category.necessary.body"),
        type: CookieType.REQUIRED,
        name: "necessary"
      }
      // For now commenting this as of now we are not using any 3rd party analytical library
      // {
      //   isOpen: false,
      //   header: copies.get("cookie.category.performance.analytics.header"),
      //   body: copies.get("cookie.category.performance.analytics.body"),
      //   type: CookieType.OPTIONAL,
      //   name: "analytical"
      // }
    ];
  }, [copies]);

  return (
    <React.Fragment>
      <div className="sliderConsentBody">
        <div className="sliderConsentBody--container">
          <div className="sliderConsentBody--manage">
            <div className="sliderConsentBody--manage-content">
              <h3>{copies.get("slider.consent.content.heading")}</h3>
              <p>{copies.get("slider.consent.content.body")}</p>
            </div>
          </div>
          <h3 className="sliderConsentBody--preference-heading">
            {copies.get("slider.consent.content.preference.heading")}
          </h3>
          <div className="sliderConsentBody--preference">
            {data.map((item, index) => {
              return (
                <React.Fragment>
                  <AccordianItem
                    key={index}
                    header={item.header}
                    body={item.body}
                    status={!!state[item.name as keyof ConsentType]}
                    type={item.type}
                    setState={setState}
                    name={item.name as keyof ConsentType}
                  />
                </React.Fragment>
              );
            })}
          </div>
        </div>
      </div>
      <div className="sliderConsentBody--footer">
        <Button
          variant={Variant.PRIMARY}
          label={copies.get("cookie.confirm.my.choices")}
          onClick={() => {
            handleConfirmChoice(state);
          }}
        />
      </div>
    </React.Fragment>
  );
}

function AccordianItem({
  body,
  header,
  status,
  type,
  setState,
  name
}: {
  body: string;
  header: string;
  status: boolean;
  type: CookieType;
  setState: React.Dispatch<React.SetStateAction<ConsentType>>;
  name: keyof ConsentType;
}) {
  const [isOpen, setIsOpen] = React.useState(false);
  const copies = useCopies();
  return (
    <div className={classNames("sliderConsentBody--preference-item", { active: isOpen })}>
      <div
        className="sliderConsentBody--preference_header"
        onClick={e => {
          setIsOpen(c => !c);
        }}>
        <div className="sliderConsentBody--preference_toggle">
          <Button icon={isOpen ? <Icon.Remove /> : <Icon.Add />} />
        </div>
        <div className="sliderConsentBody--preference_heading">
          <div className="sliderConsentBody--preference_heading--title">{header} </div>
          {type === CookieType.REQUIRED ? (
            <span className="sliderConsentBody--preference_heading--alwaysActive">
              {" "}
              {copies.get("cookie.label.always.active")}
            </span>
          ) : (
            <div
              className="sliderConsentBody--preference_required"
              onClick={e => {
                e.stopPropagation();
              }}>
              <ToggleButton
                checked={status}
                onChange={e => {
                  setState(s => ({
                    ...s,
                    [name]: !s[name]
                  }));
                }}
              />
            </div>
          )}
        </div>
      </div>
      {isOpen && (
        <div className="sliderConsentBody--preference_body">
          <p>{body}</p>
        </div>
      )}
    </div>
  );
}
